/**
 * The single way this app talks to the API.
 *
 * Design rules, in order of importance:
 *
 * 1. AUTH NEVER TOUCHES STORAGE. The refresh token lives in an httpOnly cookie
 *    the browser sends because every request sets `credentials: 'include'`. The
 *    access token lives in the module-scope `accessToken` below — a variable, in
 *    memory, gone on reload (a reload silently re-mints it from the cookie).
 *    localStorage/sessionStorage are banned by an eslint rule, not just by
 *    convention: a token in web storage is readable by any injected script.
 *
 * 2. TRANSPORT FAILURES ARE NOT HTTP FAILURES. "The server said 409" and "the
 *    request never left the phone" need different UI — one shows the server's
 *    prose, the other says "you're offline, we kept your progress". Both arrive
 *    as `ApiError`, distinguished by `kind`.
 *
 * 3. ONE ERROR SHAPE. The backend always answers failures with
 *    `{"error": {code, message, details?, request_id?}}`
 *    (backend/app/errors.py). `message` is customer-facing prose written by the
 *    server, so the UI shows it verbatim rather than inventing its own copy.
 *
 * 4. EVERY REQUEST IS TRACEABLE. We generate an `x-request-id` and send it; the
 *    backend honours an inbound value and echoes it back. It lands on ApiError
 *    so a support screenshot is enough to find the server-side log line.
 */
import type { ApiErrorCode, ApiErrorEnvelope, RateLimitDetails } from './types';

export const REQUEST_ID_HEADER = 'x-request-id';

/** How a request failed. `http` means the server answered; the rest mean it did not. */
export type ApiErrorKind = 'http' | 'network' | 'offline' | 'timeout' | 'malformed';

const DEFAULT_TIMEOUT_MS = 20_000;

// --- Access token (memory only) -------------------------------------------

let accessToken: string | null = null;

/** Sets the in-memory access token. Called by the auth layer after login/refresh. */
export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Drops the in-memory token. The httpOnly refresh cookie is cleared server-side. */
export function clearAccessToken(): void {
  accessToken = null;
}

// --- Base URL --------------------------------------------------------------

/**
 * Resolves the API origin. Compose injects VITE_API_BASE_URL; if it is missing
 * we fall back to a same-origin relative base so a misconfigured deploy degrades
 * to "requests 404" rather than "requests silently go to the wrong host".
 */
export function apiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_BASE_URL;
  const base = typeof configured === 'string' && configured.length > 0 ? configured : '';
  return base.replace(/\/+$/, '');
}

// --- Request ids -----------------------------------------------------------

/** 32 hex chars, matching the backend's `uuid4().hex`. Capped at the server's 64. */
export function newRequestId(): string {
  const cryptoObj = globalThis.crypto as Crypto | undefined;
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID().replaceAll('-', '');
  }
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  // Last resort: not cryptographically meaningful, and it does not need to be.
  return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 10)}`;
}

// --- ApiError --------------------------------------------------------------

export interface ApiErrorInit {
  code: string;
  message: string;
  status: number;
  kind: ApiErrorKind;
  details?: Record<string, unknown> | undefined;
  requestId?: string | undefined;
  cause?: unknown;
}

/**
 * Every failure out of this module. `status` is 0 when no HTTP response existed.
 */
export class ApiError extends Error {
  readonly code: string;
  /** HTTP status, or 0 for transport-level failures (offline, DNS, abort). */
  readonly status: number;
  readonly kind: ApiErrorKind;
  readonly details: Record<string, unknown> | undefined;
  readonly requestId: string | undefined;

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.kind = init.kind;
    this.details = init.details;
    this.requestId = init.requestId;
  }

  /** The device has no connectivity. Distinct from "the server is unreachable". */
  get isOffline(): boolean {
    return this.kind === 'offline';
  }

  /** The request never got an answer: offline, DNS failure, CORS block, timeout. */
  get isTransport(): boolean {
    return this.kind !== 'http';
  }

  /** Client's fault (4xx). Retrying without changing the request is pointless. */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  get isServerError(): boolean {
    return this.status >= 500;
  }

  /** Auth expired or absent — the caller should re-authenticate, not retry. */
  get isAuthError(): boolean {
    return this.status === 401;
  }

  /** Worth retrying: transport blips, 5xx, 429, and 408. */
  get isRetryable(): boolean {
    if (this.isTransport) return this.kind !== 'offline';
    return this.isServerError || this.status === 429 || this.status === 408;
  }

  /** Field-level messages from a 422 (`details.fields`), keyed by dotted path. */
  get fieldErrors(): Record<string, string> {
    const fields = this.details?.['fields'];
    if (!isRecord(fields)) return {};
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(fields)) {
      if (typeof value === 'string') out[key] = value;
    }
    return out;
  }

  /** `retry-after` guidance from a 429, in seconds. */
  get retryAfterSeconds(): number | undefined {
    const detail = (this.details as RateLimitDetails | undefined)?.retry_after_seconds;
    return typeof detail === 'number' && Number.isFinite(detail) ? detail : undefined;
  }

  /** True when `code` matches — narrows without string-comparing at call sites. */
  is(code: ApiErrorCode): boolean {
    return this.code === code;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Type guard so callers (and error boundaries) can narrow an `unknown` catch. */
export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

// --- Envelope parsing ------------------------------------------------------

/**
 * Pulls `{error: {...}}` out of a failed response body.
 *
 * Anything that is not our envelope (an nginx HTML error page, a truncated body,
 * a proxy's plain text) becomes a `malformed_response` ApiError that still
 * carries the real HTTP status — losing the status would hide "it was a 502".
 */
function errorFromBody(response: Response, body: unknown, fallbackRequestId?: string): ApiError {
  const requestId = response.headers.get(REQUEST_ID_HEADER) ?? fallbackRequestId;

  if (isRecord(body) && isRecord(body['error'])) {
    const error = body['error'];
    const code = typeof error['code'] === 'string' ? error['code'] : 'http_error';
    const message =
      typeof error['message'] === 'string' && error['message'].length > 0
        ? error['message']
        : statusFallbackMessage(response.status);
    const details = isRecord(error['details']) ? error['details'] : undefined;
    const envelopeRequestId =
      typeof error['request_id'] === 'string' ? error['request_id'] : undefined;
    return new ApiError({
      code,
      message,
      status: response.status,
      kind: 'http',
      details,
      requestId: envelopeRequestId ?? requestId ?? undefined,
    });
  }

  return new ApiError({
    code: 'malformed_response',
    message: statusFallbackMessage(response.status),
    status: response.status,
    kind: 'malformed',
    requestId: requestId ?? undefined,
    details: typeof body === 'string' && body.length > 0 ? { body: body.slice(0, 200) } : undefined,
  });
}

/** Prose for when the server did not give us any. Customer-facing, never technical. */
function statusFallbackMessage(status: number): string {
  if (status === 404) return 'We could not find what you were looking for.';
  if (status === 401) return 'Please sign in to continue.';
  if (status === 403) return 'You do not have access to this.';
  if (status === 413) return 'That file is larger than we can accept.';
  if (status === 429) return 'Too many attempts. Please wait a moment and try again.';
  if (status >= 500) return 'Something went wrong on our side. Please try again.';
  return 'The request could not be processed.';
}

/**
 * Turns a thrown fetch rejection into an ApiError.
 *
 * `fetch` rejects with a bare TypeError for everything from "airplane mode" to
 * "CORS preflight failed", so `navigator.onLine` is the only signal available to
 * separate the two. It is a coarse signal (it means "has a network interface",
 * not "can reach the internet"), which is exactly why offline is a *distinct*
 * kind rather than the default assumption.
 */
function errorFromThrown(cause: unknown, requestId: string, aborted: boolean): ApiError {
  if (aborted) {
    return new ApiError({
      code: 'timeout',
      message: 'That took too long. Please check your connection and try again.',
      status: 0,
      kind: 'timeout',
      requestId,
      cause,
    });
  }

  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (offline) {
    return new ApiError({
      code: 'offline',
      message: "You're offline. Reconnect and we'll pick up where you left off.",
      status: 0,
      kind: 'offline',
      requestId,
      cause,
    });
  }

  return new ApiError({
    code: 'network_error',
    message: "We couldn't reach the server. Please try again.",
    status: 0,
    kind: 'network',
    requestId,
    cause,
  });
}

// --- The request function --------------------------------------------------

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** Serialised as JSON unless it is FormData/Blob, which are sent as-is. */
  body?: unknown;
  /** Appended as a query string; null/undefined values are dropped. */
  query?: Record<string, string | number | boolean | null | undefined>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Defaults to 20s. Uploads should raise this deliberately. */
  timeoutMs?: number;
  /** Sent as `idempotency-key`; required by the backend on checkout-ish POSTs. */
  idempotencyKey?: string;
  /** Overrides the default `accept: application/json`. */
  accept?: string;
  /**
   * Resolve with the response body as a `Blob` instead of parsed JSON.
   *
   * For endpoints that return a file to a SIGNED-IN caller. A plain
   * `<a href>` cannot fetch one of those: the access token lives in memory in
   * this module, and a browser navigation sends cookies only — so the link
   * arrives unauthenticated and the customer gets a 401 instead of their file.
   * Fetching it here attaches the token like any other call.
   *
   * Errors are unaffected: a failure still carries a JSON envelope, and it is
   * still parsed and thrown as an `ApiError`.
   */
  asBlob?: boolean;
}

function buildUrl(path: string, query: ApiRequestOptions['query']): string {
  const base = apiBaseUrl();
  const suffix = path.startsWith('/') ? path : `/${path}`;
  let url = `${base}${suffix}`;
  if (query) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null) params.append(key, String(value));
    }
    const serialised = params.toString();
    if (serialised) url += `${url.includes('?') ? '&' : '?'}${serialised}`;
  }
  return url;
}

function isRawBody(body: unknown): body is BodyInit {
  return (
    typeof FormData !== 'undefined' &&
    (body instanceof FormData ||
      body instanceof Blob ||
      body instanceof URLSearchParams ||
      body instanceof ArrayBuffer)
  );
}

/**
 * Performs one API call and returns the parsed body, or throws `ApiError`.
 *
 * Deliberately not generic-safe: `T` is an assertion that the hand-maintained
 * type in ./types.ts matches the server. It is not validated at runtime — the
 * contract is enforced by keeping types.ts in step with the OpenAPI spec, and by
 * the backend's own response_model. Validate with zod at the edges where a
 * mismatch would corrupt money or order state.
 */
export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    query,
    headers = {},
    signal,
    timeoutMs,
    idempotencyKey,
    accept,
    asBlob = false,
  } = options;

  const requestId = newRequestId();
  const requestHeaders = new Headers({ accept: accept ?? 'application/json' });
  for (const [key, value] of Object.entries(headers)) requestHeaders.set(key, value);
  requestHeaders.set(REQUEST_ID_HEADER, requestId);
  if (idempotencyKey) requestHeaders.set('idempotency-key', idempotencyKey);
  // The CALLER'S authorization wins. This module-scope token is the CUSTOMER's;
  // the admin client passes the operator's token per call, and overwriting it
  // here sent an admin request authenticated as the customer. The server
  // verifies the audience, so it answered 401 — and only ever in a browser
  // signed in as both, which is why it survived the test suite and every
  // private window it was ever tried in.
  if (accessToken && !requestHeaders.has('authorization')) {
    requestHeaders.set('authorization', `Bearer ${accessToken}`);
  }

  let requestBody: BodyInit | undefined;
  if (body !== undefined && body !== null) {
    if (isRawBody(body)) {
      // Let the browser set the multipart boundary; never set it by hand.
      requestBody = body;
    } else {
      requestHeaders.set('content-type', 'application/json');
      requestBody = JSON.stringify(body);
    }
  }

  // Own AbortController rather than AbortSignal.any/timeout: those are missing
  // in jsdom and older Safari, and this keeps one code path everywhere.
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs ?? DEFAULT_TIMEOUT_MS);
  const onCallerAbort = () => controller.abort();
  signal?.addEventListener('abort', onCallerAbort, { once: true });

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers: requestHeaders,
      // Auth rides in cookies (httpOnly refresh token), so credentials must be
      // included on every call, including GETs, and the API's CORS config sets
      // allow_credentials with an explicit origin allowlist to match.
      credentials: 'include',
      cache: 'no-store',
      body: requestBody,
      signal: controller.signal,
    });
  } catch (cause) {
    // A caller-initiated abort (component unmount, query cancellation) is not an
    // error the user should ever see; rethrow it so TanStack Query discards it.
    if (!timedOut && signal?.aborted) throw cause;
    throw errorFromThrown(cause, requestId, timedOut);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onCallerAbort);
  }

  if (response.status === 204 || response.status === 205) {
    return undefined as T;
  }

  // Before parsing: a file response is not text, and reading a PDF through
  // `response.text()` would mangle it. Only on success — a failure still
  // answers with the JSON envelope below.
  if (asBlob && response.ok) {
    return (await response.blob()) as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('json');
  let parsed: unknown;
  try {
    parsed = isJson ? await response.json() : await response.text();
  } catch (cause) {
    if (response.ok) {
      throw new ApiError({
        code: 'malformed_response',
        message: 'We got an unexpected response from the server. Please try again.',
        status: response.status,
        kind: 'malformed',
        requestId: response.headers.get(REQUEST_ID_HEADER) ?? requestId,
        cause,
      });
    }
    parsed = undefined;
  }

  if (!response.ok) {
    throw errorFromBody(response, parsed, requestId);
  }

  /*
   * A 2xx that is not JSON is NOT a success, and treating it as one is how a
   * misconfiguration turns into silence.
   *
   * Every caller that reaches this line expects a JSON object: a file download
   * returns earlier through `asBlob`, an empty body returns earlier at
   * 204/205, and the one CSV export does its own fetch. So a `text/html` body
   * here means the request was answered by something that is not the API —
   * Electron's SPA fallback serving index.html, a captive portal, a proxy
   * error page, a dev server with no proxy configured.
   *
   * Returning that HTML to the caller is the worst outcome available. It is a
   * truthy value, so nothing throws; the caller reads the fields it wanted,
   * gets `undefined`, and carries on as though the call had worked. That is
   * exactly how signing in failed with no message on screen.
   */
  if (!isJson) {
    throw new ApiError({
      code: 'malformed_response',
      message:
        'The server answered with a web page instead of data. The app is probably ' +
        'pointed at the wrong address.',
      status: response.status,
      kind: 'malformed',
      requestId: response.headers.get(REQUEST_ID_HEADER) ?? requestId,
      details: {
        contentType: contentType || '(none)',
        url: response.url,
        body: typeof parsed === 'string' ? parsed.slice(0, 200) : undefined,
      },
    });
  }

  return parsed as T;
}

/** Thin verb helpers. Everything funnels through `apiRequest`. */
export const api = {
  get: <T>(path: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
} as const;

/** Coerces any thrown value into an ApiError so UI code has one shape to render. */
export function toApiError(value: unknown): ApiError {
  if (isApiError(value)) return value;
  return new ApiError({
    code: 'internal_error',
    message: 'Something unexpected happened. Please try again.',
    status: 0,
    kind: 'network',
    cause: value,
  });
}

/** The API envelope for a typed error envelope, exported for tests and mocks. */
export type { ApiErrorEnvelope };
